import { useMemo } from 'react';
import {
  Bouton, Etiquette, IconeCorbeille, IconePlus, SaisieDifferee, paveTeinte,
  useColonnes, useTheme,
} from '@journal/ui';

/**
 * Tableau de la période : les SEMAINES en lignes, les sous-domaines en colonnes.
 *
 * Reprend `Progression.renderTable` de l'application d'origine, orientation
 * comprise. Elle n'est pas cosmétique : une période fait sept à huit semaines
 * et un domaine en compte quatre ou cinq. Les semaines en lignes donnent un
 * tableau qui descend — le sens dans lequel on lit un déroulé — au lieu d'un
 * tableau qui part vers la droite et qu'il faut faire défiler pour voir la fin
 * de la période.
 *
 * ── Ce que l'affichage semaine par semaine ne montrait pas ──────────────────
 * Préparer une progression, c'est répartir des notions sur toute la période.
 * La question est « où en suis-je en lecture d'ici les vacances ? » : elle se
 * lit sur une colonne, du haut vers le bas. Un écran qui n'affiche qu'une
 * semaine ne montre jamais la progression, seulement son instantané.
 *
 * ── Une séance = un titre + des sous-bulles numérotées ──────────────────────
 * Le titre dit ce qu'on fait ; les sous-bulles détaillent, une par ligne du
 * champ `competencies`. C'est cette découpe qui permet de reporter une partie
 * seulement d'une séance — le cas ordinaire du vendredi soir.
 */

export interface LigneSemaine {
  lundi: string;
  vendredi: string;
  rang: number;
  /** Semaine de vacances : grisée, et l'on n'y ajoute rien. */
  vacances: boolean;
  /** Semaine en cours : repère visuel, comme dans l'original. */
  courante: boolean;
}

/*
 * ── La couleur de la matière : UNE fois, et pas cinq ────────────────────────
 *
 * Elle servait ici à cinq endroits à la fois : fond plein de l'en-tête
 * « Semaine » en blanc sur couleur, filet de 3 px sous chaque en-tête de
 * sous-domaine, barre de 4 px sur chaque ligne de semaine, barre de 3 px sur
 * chaque carte de séance, et teinte du texte d'un bouton. Sur une période de
 * six semaines et six sous-domaines, cela faisait une cinquantaine d'aplats de
 * la même teinte.
 *
 * Or le tableau n'affiche QU'UNE matière à la fois. La couleur y est donc la
 * même partout : elle ne distingue rien, elle décore. Une couleur qui ne porte
 * aucune information et qu'on répète cinquante fois n'aide pas à lire — elle
 * fatigue, et elle fait passer pour une erreur d'affichage ce qui était voulu.
 *
 * Elle est conservée à UN endroit : un filet en tête du tableau, qui dit de
 * quelle matière on parle. Le reste passe aux teintes neutres de l'interface,
 * qui sont faites pour structurer sans crier.
 */

export interface ColonneSousDomaine {
  /** `null` pour « Général », qui recueille les séances non rattachées. */
  id: string | null;
  nom: string;
  /** Tous les identifiants de domaine portant ce nom (un par niveau). */
  ids: string[];
}

export interface SequenceCellule {
  id: string;
  curriculumDomainId: string | null;
  weekStart: string;
  sequence: string;
  /** Sous-bulles, une par ligne. */
  competencies: string;
  content: string;
  done: boolean;
  reportedFromId: string | null;
}

/** Découpe le champ multi-ligne en sous-bulles, à l'identique du serveur. */
export function enSousBulles(texte: string): string[] {
  return texte === '' ? [] : texte.split('\n');
}

/**
 * Séquences indexées par cellule.
 *
 * Sans cet index, chaque case rebalaie la liste ENTIÈRE : le produit du nombre
 * de semaines, de colonnes et de séances, à chaque rendu — donc à chaque
 * frappe dans n'importe quelle case.
 */
export function indexerSequences(
  sequences: SequenceCellule[],
): Map<string, SequenceCellule[]> {
  const index = new Map<string, SequenceCellule[]>();
  for (const s of sequences) {
    const cle = `${s.curriculumDomainId ?? ''}|${s.weekStart}`;
    const liste = index.get(cle);
    if (liste) liste.push(s); else index.set(cle, [s]);
  }
  return index;
}

/** Séances d'une case : la colonne rassemble tous ses identifiants de domaine. */
export function sequencesDeLaCase(
  index: Map<string, SequenceCellule[]>,
  colonne: ColonneSousDomaine,
  lundi: string,
): SequenceCellule[] {
  const cles = colonne.id === null ? [''] : colonne.ids;
  return cles
    .flatMap((id) => index.get(`${id}|${lundi}`) ?? [])
    /*
     * Les séances REPORTÉES d'abord, puis les autres.
     *
     * C'est l'ordre de l'original, et il a une raison : ce qui vient d'être
     * repoussé de la semaine précédente est ce qu'il faut traiter en premier.
     * L'enfouir sous les séances prévues, c'est le reporter une fois de plus.
     */
    .sort((a, b) => {
      if (a.reportedFromId && !b.reportedFromId) return -1;
      if (!a.reportedFromId && b.reportedFromId) return 1;
      return 0;
    });
}

/**
 * Sous-bulles déjà reportées, déduites de la copie de la semaine suivante.
 *
 * Rien n'enregistre « cette ligne est reportée » : la vérité est la copie
 * elle-même. On rapproche donc les textes. Un état séparé se serait
 * désynchronisé au premier report annulé à la main.
 */
export function bullesReportees(
  sequence: SequenceCellule,
  toutes: SequenceCellule[],
): { actif: boolean; set: Set<number> } {
  const copie = toutes.find((x) => x.reportedFromId === sequence.id);
  if (!copie) return { actif: false, set: new Set() };

  const lignesCopiees = enSousBulles(copie.competencies)
    .map((t) => t.trim())
    .filter(Boolean);
  const set = new Set<number>();
  enSousBulles(sequence.competencies).forEach((t, i) => {
    if (t.trim() && lignesCopiees.includes(t.trim())) set.add(i);
  });
  return { actif: true, set };
}

/**
 * La séance est-elle reportée EN ENTIER ?
 *
 * Seuls comptent les détails écrits : un détail vide ne part jamais dans la
 * copie, et l'exiger rendait la croix du titre grise à jamais pour une séance
 * dont on avait ajouté des cases sans les remplir. Sans détail écrit, la
 * séance l'est dès qu'une copie existe — c'est son titre qui est parti.
 */
export function seanceToutReportee(
  sequence: SequenceCellule,
  toutes: SequenceCellule[],
): boolean {
  const rep = bullesReportees(sequence, toutes);
  if (!rep.actif) return false;
  return enSousBulles(sequence.competencies)
    .every((t, i) => !t.trim() || rep.set.has(i));
}

const dateCourte = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', timeZone: 'UTC',
  });

/**
 * Croix de report.
 *
 * Rouge quand la ligne est déjà reportée, pâle sinon — et le même bouton
 * annule. Un geste, deux sens, comme dans l'original : c'est ce qui permet de
 * corriger une erreur de clic sans chercher où défaire.
 */
function CroixReport({
  actif, surClic, libelle,
}: { actif: boolean; surClic: () => void; libelle: string }) {
  return (
    <button
      type="button"
      onClick={surClic}
      title={libelle}
      aria-label={libelle}
      aria-pressed={actif}
      className={[
        'shrink-0 rounded px-1 text-[13px] font-extrabold leading-none transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]',
        actif
          ? 'text-[var(--color-danger)]'
          /*
           * `fg-subtle` et non `border-strong` : ce dernier est calibré pour un
           * CONTOUR — 3:1 —, seuil qui ne suffit pas à un signe qu'on lit.
           * Mesuré à 3,26:1 en thème clair, il passait tout juste pour un
           * élément graphique et échouait comme texte.
           */
          : 'text-[var(--color-fg-subtle)] hover:text-[var(--color-danger)]',
      ].join(' ')}
    >
      ✖
    </button>
  );
}

export function TableauProgressions({
  semaines, sousDomaines, sequences, pourLesCroix = sequences, couleur,
  surAjoutSeance, surModificationTitre, surModificationBulle,
  surAjoutBulle, surSuppressionBulle, surSuppression,
  surReportSeance, surReportBulle,
}: {
  semaines: LigneSemaine[];
  sousDomaines: ColonneSousDomaine[];
  sequences: SequenceCellule[];
  /**
   * Où chercher les copies qui colorent les croix : par défaut la période, plus
   * celles qu'un report a fait passer dans la période suivante.
   */
  pourLesCroix?: SequenceCellule[];
  couleur: string;
  surAjoutSeance: (colonne: ColonneSousDomaine, lundi: string) => void;
  surModificationTitre: (sequence: SequenceCellule, texte: string) => Promise<boolean>;
  surModificationBulle: (
    sequence: SequenceCellule, index: number, texte: string,
  ) => Promise<boolean>;
  surAjoutBulle: (sequence: SequenceCellule) => void;
  surSuppressionBulle: (sequence: SequenceCellule, index: number) => void;
  surSuppression: (sequence: SequenceCellule) => void;
  surReportSeance: (sequence: SequenceCellule) => void;
  surReportBulle: (sequence: SequenceCellule, index: number) => void;
}) {
  const parCellule = useMemo(() => indexerSequences(sequences), [sequences]);
  const colonnes = useColonnes('progressions');
  // La couleur de la matière ne s'écrit jamais telle quelle : elle passe par
  // le pavé, qui en tire un fond, une encre et un filet faits l'un pour l'autre.
  const { theme } = useTheme();
  const pave = paveTeinte(couleur, theme);

  /*
   * UN SEUL tableau, celui de l'écran.
   *
   * Il y avait ici, en plus, des copies réservées à l'impression : une par bloc
   * de cinq colonnes, masquées à l'écran. C'était la première façon de découper
   * une progression large sur plusieurs feuilles, du temps où l'on imprimait la
   * page elle-même.
   *
   * Elles nuisent maintenant. L'impression construit son propre document à
   * partir du tableau affiché et le découpe elle-même ; trouvant ces copies
   * dans la page, elle découpait un bloc DÉJÀ découpé — et rendait deux
   * feuilles ne portant que la colonne « Semaine ».
   */
  const rendreTableau = (
    colonnesBloc: ColonneSousDomaine[], cle: string, classe: string,
  ) => (
      <table key={cle} className={classe}>
        {colonnes.colgroup(['Semaine', ...colonnesBloc.map((sd) => sd.id ?? 'general')])}
        <caption className="sr-only">
          Semaines de la période en lignes, sous-domaines en colonnes.
        </caption>
        <thead>
          <tr>
            <th
              scope="col"
              className="relative sticky left-0 z-10 w-[7rem] border-b border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]"
              style={colonnes.entete('Semaine').style}
            >
              Semaine
              {colonnes.poignee('Semaine')}
            </th>
            {colonnesBloc.map((sd) => (
              <th
                key={sd.id ?? 'general'}
                scope="col"
                className="relative min-w-[13rem] border-b border-l border-[var(--color-border)] bg-[var(--color-surface-raised)] px-2 py-2 text-center align-top text-[13px] font-semibold"
                // Après la classe `min-w-[13rem]`, qu'il faut pouvoir lever :
                // c'est elle qui rendait le réglage des colonnes inopérant.
                style={colonnes.entete(sd.id ?? 'general').style}
              >
                {colonnes.poignee(sd.id ?? 'general')}
                {sd.nom}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {semaines.map((semaine) => (
            <tr
              key={semaine.lundi}
              className={[
                'border-t border-[var(--color-border)]',
                semaine.vacances ? 'bg-[var(--color-surface-zebra)]' : '',
                semaine.courante ? 'ring-1 ring-inset ring-[var(--color-primary)]' : '',
              ].join(' ')}
            >
              <th
                scope="row"
                /*
                 * Intitulé explicite : les trois lignes de la case se collent
                 * autrement en « S17 sept.11 sept. », illisible à l'oreille
                 * comme à la relecture — et ambigu, « S1 » ne se distinguant
                 * plus de « S17 ».
                 */
                aria-label={`Semaine ${semaine.rang}, du ${dateCourte(semaine.lundi)} au ${dateCourte(semaine.vendredi)}${semaine.vacances ? ' (vacances)' : ''}`}
                className="sticky left-0 z-10 border-l-4 border-l-transparent bg-[var(--color-surface)] px-3 py-2 text-left align-top"
              >
                <span className="block text-[13px] font-bold">{`S${semaine.rang}`}</span>
                <span className="block text-[10px] font-normal text-[var(--color-fg-muted)]">
                  {dateCourte(semaine.lundi)}
                </span>
                <span className="block text-[10px] font-normal text-[var(--color-fg-muted)]">
                  {dateCourte(semaine.vendredi)}
                </span>
              </th>

              {colonnesBloc.map((colonne) => {
                const dedans = sequencesDeLaCase(parCellule, colonne, semaine.lundi);
                return (
                  <td
                    key={colonne.id ?? 'general'}
                    className="border-l border-[var(--color-border)] p-1.5 align-top"
                  >
                    <div className="flex flex-col gap-1.5">
                      {dedans.map((sequence) => {
                        const bulles = enSousBulles(sequence.competencies);
                        const rep = bullesReportees(sequence, pourLesCroix);
                        const toutReporte = seanceToutReportee(sequence, pourLesCroix);

                        return (
                          <div
                            key={sequence.id}
                            /*
                             * Une carte neutre. Ce qui doit se voir sur une
                             * séance, ce sont ses ÉTIQUETTES — « Reporté »,
                             * « Traité » — qui portent une information ; pas
                             * une barre de couleur identique sur les
                             * cinquante autres.
                             */
                            className="rounded-lg border border-[var(--color-border)] p-1.5"
                            style={{
                              background: sequence.reportedFromId
                                ? 'var(--color-surface-raised)'
                                : 'var(--color-surface)',
                            }}
                          >
                            <div className="mb-1 flex flex-wrap items-center gap-1">
                              {sequence.reportedFromId && (
                                <Etiquette ton="attention">Reporté</Etiquette>
                              )}
                              {sequence.done && <Etiquette ton="succes">Traité</Etiquette>}
                            </div>

                            <div className="flex items-start gap-1.5">
                              <div className="min-w-0 flex-1">
                                <SaisieDifferee
                                  libelle={
                                    `Séance · ${colonne.nom}, semaine ${semaine.rang}`
                                  }
                                  valeur={sequence.sequence}
                                  surEnregistrement={(v) => surModificationTitre(sequence, v)}
                                />
                              </div>
                              <CroixReport
                                actif={toutReporte}
                                surClic={() => surReportSeance(sequence)}
                                libelle={toutReporte
                                  ? `Annuler le report de « ${sequence.sequence || 'la séance'} »`
                                  : `Reporter « ${sequence.sequence || 'la séance'} » à la semaine suivante`}
                              />
                              <Bouton
                                variante="ghost"
                                taille="sm"
                                iconeSeule
                                icone={<IconeCorbeille taille={12} />}
                                onClick={() => surSuppression(sequence)}
                                aria-label={`Supprimer « ${sequence.sequence || 'la séance'} »`}
                              >
                                Supprimer
                              </Bouton>
                            </div>

                            {bulles.map((texte, index) => (
                              <div
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                /*
                                 * Détail reporté : fond et filet rouges pâles,
                                 * comme dans l'application d'origine. La croix
                                 * seule, rouge sur dix pixels, ne se voyait pas
                                 * en parcourant la semaine.
                                 */
                                className={[
                                  'mt-1 flex items-start gap-1.5 rounded-md border p-1',
                                  rep.set.has(index)
                                    ? 'border-[var(--color-danger)]/30 bg-[var(--color-danger-soft)]'
                                    : 'border-[var(--color-border)]',
                                ].join(' ')}
                              >
                                <span
                                  className="mt-1 shrink-0 text-[10px] font-extrabold"
                                  style={{ color: pave.encre }}
                                >
                                  {`${index + 1}.`}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <SaisieDifferee
                                    libelle={`Détail ${index + 1} · ${colonne.nom}`}
                                    valeur={texte}
                                    surEnregistrement={
                                      (v) => surModificationBulle(sequence, index, v)
                                    }
                                  />
                                </div>
                                <CroixReport
                                  actif={rep.set.has(index)}
                                  surClic={() => surReportBulle(sequence, index)}
                                  libelle={rep.set.has(index)
                                    ? `Annuler le report du détail ${index + 1}`
                                    : `Reporter le titre et le détail ${index + 1} à la semaine suivante`}
                                />
                                <Bouton
                                  variante="ghost"
                                  taille="sm"
                                  iconeSeule
                                  icone={<IconeCorbeille taille={11} />}
                                  onClick={() => surSuppressionBulle(sequence, index)}
                                  aria-label={`Supprimer le détail ${index + 1}`}
                                >
                                  Supprimer
                                </Bouton>
                              </div>
                            ))}

                            <Bouton
                              variante="ghost"
                              taille="sm"
                              onClick={() => surAjoutBulle(sequence)}
                              aria-label={
                                `Ajouter un détail à « ${sequence.sequence || 'la séance'} »`
                              }
                              className="mt-1 w-full border border-dashed"
                              /*
                               * La teinte brute de la matière servait d'encre :
                               * 3,15:1 pour le bleu de « Français » sur fond
                               * blanc. L'encre dérivée du pavé est faite pour
                               * être lue, dans les deux thèmes.
                               */
                              style={{ borderColor: pave.filet, color: pave.encre }}
                            >
                              +
                            </Bouton>
                          </div>
                        );
                      })}

                      {semaine.vacances ? (
                        <p className="px-1 py-2 text-center text-[11px] text-[var(--color-fg-muted)]">
                          🌴 Vacances
                        </p>
                      ) : (
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          icone={<IconePlus taille={13} />}
                          onClick={() => surAjoutSeance(colonne, semaine.lundi)}
                          aria-label={
                            `Ajouter une séance · ${colonne.nom}, semaine ${semaine.rang}`
                          }
                          className="w-full border border-dashed border-[var(--color-border-strong)]"
                        >
                          Séance
                        </Bouton>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
  );

  return (
    <div
      role="region"
      aria-label="Progression de la période, par semaine et par sous-domaine"
      data-impression="progressions"
      tabIndex={0}
      className="impression-paysage overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--ombre-carte)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
    >
      {/*
        * À l'écran : UN tableau, toutes les colonnes, qui défile
        * horizontalement. C'est ce qu'on veut pour préparer une période — voir
        * la matière entière d'un seul mouvement.
        */}
      {/*
        * Le seul aplat de couleur de l'écran : il dit de quelle matière il
        * s'agit, une fois, en haut. C'est tout ce qu'une couleur peut faire
        * ici sans devenir du bruit.
        */}
      <div aria-hidden="true" style={{ height: 3, background: couleur }} />

      {rendreTableau(sousDomaines, 'ecran', 'w-full border-collapse text-sm')}
    </div>
  );
}
